import { InstalledAppleApplication } from '@rocksoft/locations-sdk';
import {
  CategoryApplicationDto,
  CategoryAppLockDto,
  CategoryConfigurationDto,
  CategoryConfigurationInheritanceStructureDto,
  CategoryType,
  CategoryVppApplicationDto,
} from '@rocksoft/settings-sdk';
import type { VppApplication } from '@rocksoft/shared';
import { Device } from '@rocksoft/shared';

/**
 * This file contains helper types and functions for working with inherited category configurations in the context of a category configuration inheritance structure.
 * It provides types for inherited configuration items and application settings, as well as functions to collect and select inherited configurations and settings from a given inheritance structure.
 */

// ============================================================================
// HELPER TYPES
// ============================================================================

/**
 * Metadata interface for inherited category configuration items.
 * It includes optional properties for categoryId and categoryType, which can be used to identify the origin of the configuration item.
 */
export interface InheritedCategoryConfigurationMetadata {
  categoryId: string;
  categoryType: CategoryType;
}

/**
 * Type for inherited category configuration items.
 * It extends the generic type T with the InheritedCategoryConfigurationMetadata interface,
 * allowing for the inclusion of metadata about the origin of the configuration item.
 */
export type InheritedCategoryConfigurationItem<T extends object> = T & InheritedCategoryConfigurationMetadata;

export type InheritedApplicationConfiguration = InheritedCategoryConfigurationItem<CategoryApplicationDto>;

export type InheritedVppApplicationConfiguration = Omit<
  InheritedCategoryConfigurationItem<CategoryVppApplicationDto>,
  'applyFrom'
> &
  Partial<VppApplication> & {
    applyFrom?: Date;
  };

export type ApplicationPresentationConfiguration = {
  bundleIdentifier?: string | null;
  version?: string | null;
  displayName?: string | null;
  applyFrom?: Date | string;
  applyTill?: Date | string | null;
  state?: ApplicationVersionState;
  categoryId: string;
  categoryType: string;
};

export type ApplicationVersionState = 'to-be-installed' | 'active' | 'scheduled' | 'previous';

export type ApplicationPresentationGroup<T extends ApplicationPresentationConfiguration> = (T & {
  state: ApplicationVersionState;
})[];

/**
 * Sorts versions for display with the currently active version first.
 * Versions in the same state are ordered by the latest applyFrom date first.
 */
export function sortApplicationVersionsForPresentation<
  T extends { applyFrom?: Date | string | null; state: ApplicationVersionState },
>(applications: T[]): T[] {
  const stateOrder: Record<ApplicationVersionState, number> = {
    'to-be-installed': 0,
    active: 1,
    scheduled: 2,
    previous: 3,
  };

  return [...applications].sort((left, right) => {
    const stateDifference = stateOrder[left.state] - stateOrder[right.state];
    if (stateDifference !== 0) {
      return stateDifference;
    }

    return getApplyFromTimestamp(right.applyFrom) - getApplyFromTimestamp(left.applyFrom);
  });
}

function getApplyFromTimestamp(applyFrom?: Date | string | null): number {
  if (!applyFrom) {
    return 0;
  }

  const timestamp = new Date(applyFrom).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

export const normalizeAppLockModels = <T extends CategoryAppLockDto>(appLock: T) => ({
  ...appLock,
  models: appLock.models ?? [],
});

// ============================================================================
// GENERAL HELPER FUNCTIONS
// ============================================================================

/**
 * Adds metadata about the origin of a configuration item to the item itself.
 * @param categoryConfiguration - The complete CategoryConfigurationDto object for Metadata extraction, which contains the categoryId and categoryType.
 * @param configuration - The specific SettingType Configuration (e.g., Application, VPP Application, App Lock, etc.) to which the metadata will be added
 * @returns The configuration item with added metadata about its origin
 */
export function withCategoryOrigin<T extends object>(
  categoryConfiguration: CategoryConfigurationDto,
  configuration: T
): InheritedCategoryConfigurationItem<T> {
  return {
    ...configuration,
    categoryId: categoryConfiguration.id,
    categoryType: categoryConfiguration.categoryType,
  };
}

/**
 * Collects inherited configuration items from a category configuration inheritance structure.
 * @param inheritedConfiguration - The current category configuration inheritance structure.
 * @param selectConfigurations - A function to select specific configurations from a category configuration.
 * @param selectIdentifier - A function to select a unique identifier for each configuration item.
 * @param seenIdentifiers - A set of identifiers that have already been processed to avoid duplicates.
 * @returns An array of inherited configuration items with metadata about their origin.
 */
export function collectInheritedConfigurations<T extends object>(
  inheritedConfiguration: CategoryConfigurationInheritanceStructureDto | undefined,
  selectConfigurations: (configuration: CategoryConfigurationDto) => T[] | null | undefined,
  selectIdentifier: (configuration: T) => string,
  seenIdentifiers: Set<string> = new Set()
): InheritedCategoryConfigurationItem<T>[] {
  if (!inheritedConfiguration) {
    return [];
  }

  const currentConfigurations =
    selectConfigurations(inheritedConfiguration.categoryConfiguration)
      ?.filter((configuration) => {
        const identifier = selectIdentifier(configuration);
        if (seenIdentifiers.has(identifier)) {
          return false;
        }

        seenIdentifiers.add(identifier);
        return true;
      })
      .map((configuration) => withCategoryOrigin(inheritedConfiguration.categoryConfiguration, configuration)) ?? [];

  return [
    ...currentConfigurations,
    ...collectInheritedConfigurations(
      inheritedConfiguration.ancestor,
      selectConfigurations,
      selectIdentifier,
      seenIdentifiers
    ),
  ];
}

/**
 * Selects the first inherited configuration item from a collection on the current category or its ancestors
 * that matches the provided predicate.
 */
export function selectFirstMatchingInheritedConfiguration<T extends object>(
  inheritedConfiguration: CategoryConfigurationInheritanceStructureDto | undefined,
  selectConfigurations: (configuration: CategoryConfigurationDto) => T[] | null | undefined,
  predicate: (configuration: T) => boolean
): InheritedCategoryConfigurationItem<T> | null {
  if (!inheritedConfiguration) {
    return null;
  }

  const currentConfiguration = selectConfigurations(inheritedConfiguration.categoryConfiguration)?.find(predicate);
  if (currentConfiguration) {
    return withCategoryOrigin(inheritedConfiguration.categoryConfiguration, currentConfiguration);
  }

  return selectFirstMatchingInheritedConfiguration(inheritedConfiguration.ancestor, selectConfigurations, predicate);
}

// ============================================================================
// 'APPLICATION CONFIGURATION'-SPECIFIC HELPER FUNCTIONS
// ============================================================================

/**
 * Groups inherited application configurations by their bundle identifier.
 * @param applications - An array of inherited application configurations to be grouped.
 * @returns A record where the keys are bundle identifiers and the values are arrays of inherited application configurations with that bundle identifier.
 */
export function groupApplicationsByBundleIdentifier(applications: InheritedApplicationConfiguration[]) {
  return applications.reduce<Record<string, InheritedApplicationConfiguration[]>>((groups, application) => {
    const bundleIdentifier = application.bundleIdentifier;
    if (!bundleIdentifier) {
      return groups;
    }

    groups[bundleIdentifier] ??= [];
    groups[bundleIdentifier].push(application);
    return groups;
  }, {});
}

/**
 * Groups applications by bundle identifier and filters out superseded versions for display.
 * This is a presentation rule only; the inheritance decision comes from the inherited API.
 */
export function groupApplicationsForPresentation<T extends ApplicationPresentationConfiguration>(
  applications: T[]
): ApplicationPresentationGroup<T>[] {
  const groupedApplications = groupApplicationsByBundleIdentifier(applications as InheritedApplicationConfiguration[]);
  const groupedApplicationsArray = Object.values(groupedApplications as Record<string, T[]>);
  const filteredGroupedApplications = filterApplicationsForPresentation(groupedApplicationsArray);

  return filteredGroupedApplications.map((group: T[]) => {
    const versions = group.map((app: T) => {
      const state = getPresentationState(app, group);

      return { ...app, state };
    });

    return sortApplicationVersionsForPresentation(versions);
  });
}

function getPresentationState<T extends ApplicationPresentationConfiguration>(
  application: T,
  group: T[]
): ApplicationVersionState {
  if (application.state === 'to-be-installed') {
    return application.state;
  }

  const activeApp = getActiveApplicationForPresentation(group);

  if (application === activeApp) {
    return 'active';
  }

  if (application.applyFrom && new Date(application.applyFrom) > new Date()) {
    return 'scheduled';
  }

  return 'previous';
}

/**
 * Presentation-only filter for grouped application versions.
 *
 * Input: an array of groups where each group contains all versions for a single bundle identifier
 * (e.g. [room-level versions, location-level versions, ...]).
 *
 * Output: for each group, return a subset chosen for UI presentation according to specificity and active-state rules:
 *  - Prefer more-specific category types when that level contains an "active" version. Specificity order used here is
 *    Room > Location.
 *  - A version is considered "active" when `getActiveApplicationForPresentation` returns it. That helper returns the
 *    latest version whose `applyFrom` is not in the future; it may return `undefined` when no version is currently active.
 *    This function relies on the truthiness of that result to detect an active version at a level.
 *  - If a Room-level active version exists, only Room-level versions are returned (including scheduled ones).
 *  - Otherwise, if a Location-level active version exists, only Location-level versions are returned.
 *  - Otherwise, no level has an active version and all versions across levels are returned (no filtering).
 *
 * Notes:
 *  - This is strictly a UI/presentation rule: it does not change inheritance or the canonical configuration decision
 *    provided by the `/inherited` API — it only reduces the list shown to users for clarity.
 *  - Because `getActiveApplicationForPresentation(...)` can be falsy/undefined, callers should treat its result
 *    as a presence check (truthy = has active version).
 * - This only applies to Brand/Location/Room inheritance; Other Categories don't have inheritance for applications.
 */
function filterApplicationsForPresentation<T extends ApplicationPresentationConfiguration>(groupedApplications: T[][]) {
  return groupedApplications.map((apps: T[]) => {
    if (apps.length === 1) {
      return apps;
    }

    const roomLevelApps = apps.filter((app: T) => app.categoryId != null && app.categoryType === 'Room');
    const hasRoomLevelConfiguration = roomLevelApps.length > 0;

    const locationLevelApps = apps.filter((app: T) => app.categoryId != null && app.categoryType === 'Location');
    const hasLocationLevelConfiguration = locationLevelApps.length > 0;

    if (hasRoomLevelConfiguration && getActiveApplicationForPresentation(roomLevelApps)) {
      return roomLevelApps;
    }

    if (hasLocationLevelConfiguration && getActiveApplicationForPresentation(locationLevelApps)) {
      return locationLevelApps;
    }

    return apps;
  });
}

/**
 * Selects the first active inherited application configuration from a list of inherited application configurations.
 * @param applications - An array of inherited application configurations to be checked.
 * @returns The first active inherited application configuration, or undefined if none are active.
 */
export function getActiveApplicationForPresentation<T extends ApplicationPresentationConfiguration>(applications: T[]) {
  const now = Date.now();

  return [...applications]
    .filter((application) => application.state !== 'to-be-installed')
    .sort((left, right) => new Date(right.applyFrom ?? 0).getTime() - new Date(left.applyFrom ?? 0).getTime())
    .find((application) => new Date(application.applyFrom ?? 0).getTime() <= now);
}

/**
 * Selects the first active inherited application configuration from a list of inherited application configurations.
 * @param applications - An array of inherited application configurations to be checked.
 * @returns The first active inherited application configuration, or undefined if none are active.
 */
export function selectActiveInheritedApplication(applications: InheritedApplicationConfiguration[]) {
  return getActiveApplicationForPresentation(applications);
}

/**
 * Adds an `isConfiguredVersion` flag to the managed applications of an Apple device.
 * @param device - The Apple device to be updated.
 * @param applications - The list of inherited application configurations to check against.
 * @returns The updated Apple device with the `isConfiguredVersion` flag added to its managed applications, or undefined if the device is undefined.
 */
export const addIsConfiguredFlagToAppleDevice = (
  device: Device | undefined,
  applications: InheritedApplicationConfiguration[]
): Device | undefined => {
  if (device === undefined) {
    return undefined;
  }

  if (!device.managedApplications) {
    return device;
  }

  const applicationsWithFlag = device.managedApplications.map((managedApplication) => ({
    ...managedApplication,
    isConfiguredVersion: isConfiguredVersion(managedApplication, applications),
  }));

  return {
    ...device,
    managedApplications: applicationsWithFlag,
  };
};

const isConfiguredVersion = (
  application: InstalledAppleApplication,
  applications: InheritedApplicationConfiguration[]
): boolean => {
  const configuredApplication = applications.find((app) => app.bundleIdentifier === application.identifier);

  if (configuredApplication === undefined) {
    return true;
  }

  return configuredApplication.version === application.version;
};

// ============================================================================
// 'VPP APPLICATION CONFIGURATION'-SPECIFIC HELPER FUNCTIONS
// ============================================================================

/**
 * Enriches VPP applications with metadata from the VPP store.
 * @param applications - The list of inherited VPP application configurations.
 * @param vppApplications - The list of VPP applications from the VPP store.
 * @returns The list of enriched VPP application configurations.
 */
export const enrichVppApplications = (
  applications: InheritedCategoryConfigurationItem<CategoryVppApplicationDto>[],
  vppApplications: VppApplication[]
): InheritedVppApplicationConfiguration[] => {
  const vppApplicationsByAdamId = new Map(vppApplications.map((application) => [application.adamIdStr, application]));

  return applications.map((application) => {
    const metadata = application.adamIdStr ? vppApplicationsByAdamId.get(application.adamIdStr) : undefined;

    return {
      ...application,
      ...metadata,
      version: metadata?.version ?? application.version ?? '',
      adamIdStr: metadata?.adamIdStr ?? application.adamIdStr ?? '',
      categoryId: application.categoryId,
      categoryType: application.categoryType,
      bundleId: metadata?.bundleId ?? application.bundleIdentifier ?? undefined,
      name: metadata?.name ?? undefined,
      applyFrom: application.applyFrom ? new Date(application.applyFrom) : undefined,
    };
  });
};
